"use client"

import { QrCode, ShoppingBag, UserRound } from "lucide-react"
import Link from "next/link"

import { localizeWebAccountPath } from "@/lib/account-links"

import { accountText, type LocaleProps } from "../../personal-web"

/**
 * Public-guide placeholder shown in place of the real QRCard on the
 * /installation-guide page. Same visual language as QRCard (orange aura,
 * dot-grid backdrop, rounded white panel) so the page rhythm is identical
 * to the logged-in account/esim/{id}/installation view — only the data is
 * different.
 *
 * Where the real QRCard renders a scannable QR image + Save button, this
 * placeholder renders an explanatory icon + two CTAs:
 *   1. "I already have an eSIM" → /account/esim (forces login)
 *   2. "I haven't bought yet"   → / (homepage, country picker)
 *
 * No secrets / LPA / SM-DP+ data is ever shown here — that's the point.
 */
export function QRCardPlaceholder({ lng }: LocaleProps) {
  const myEsimHref = localizeWebAccountPath("/account/esim", lng)
  const shopHref = `/${lng}`

  return (
    <section className="relative overflow-hidden rounded-2xl border border-stone-200 bg-white p-5 shadow-[0_8px_30px_-12px_rgba(28,25,23,0.12)] md:p-6">
      {/* Dot-grid decorative backdrop — identical to QRCard */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.3]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgb(244 215 191) 1px, transparent 0)",
          backgroundSize: "22px 22px",
          maskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 70%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 70%)",
        }}
      />
      <div className="relative grid gap-5 md:grid-cols-[240px_minmax(0,1fr)] md:items-center md:gap-6">
        {/* Icon panel — replaces the QR image */}
        <div className="mx-auto w-full max-w-[240px]">
          <div className="relative">
            <div
              aria-hidden
              className="absolute -inset-3 -z-10 rounded-[28px] bg-gradient-to-br from-orange-100 via-amber-100 to-orange-200 blur-xl"
            />
            <div className="flex aspect-square w-full flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-stone-300 bg-white p-6 shadow-[0_4px_16px_-6px_rgba(28,25,23,0.12)]">
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-50 text-orange-500">
                <QrCode className="h-8 w-8" aria-hidden />
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <h3 className="text-lg font-extrabold tracking-tight text-slate-950 md:text-xl">
            {accountText(lng, "install.guide.qrPlaceholderTitle")}
          </h3>
          <p className="text-[15px] leading-7 text-slate-600">
            {accountText(lng, "install.guide.qrPlaceholderBody")}
          </p>
          <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <Link
              href={myEsimHref}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-orange-500 px-5 text-sm font-semibold text-white transition hover:bg-orange-600"
            >
              <UserRound className="h-4 w-4" aria-hidden />
              {accountText(lng, "install.guide.ctaViewMyEsim")}
            </Link>
            <Link
              href={shopHref}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-stone-300 bg-white px-5 text-sm font-semibold text-stone-800 transition hover:border-stone-400 hover:bg-stone-50"
            >
              <ShoppingBag className="h-4 w-4" aria-hidden />
              {accountText(lng, "install.guide.ctaShop")}
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}
